/**
 * Fantasy Console 0 - Map Actions Component
 *
 * Map-wide operations: clear, fill, zoom controls, grid toggle,
 * and import/export.
 */

import { useCallback, useRef } from 'react'
import {
  clearMap,
  fillMapWithTile,
  downloadMapAsCsv,
  importMapFromFile,
  exportMapAsPng,
} from '../../utils/mapUtils'
import { useConfirmDialog } from '../UI/ConfirmDialog'
import { ActionGroup, IconButton } from '../shared/ActionGroup'
import { ClearIcon, ImportIcon, ExportIcon } from '../shared/icons'
import styles from './MapActions.module.css'
import editorStyles from '../../styles/editor.module.css'

interface MapActionsProps {
  mapData: Uint8Array
  spriteData: Uint8Array
  selectedTile: number
  onMapChange: (map: Uint8Array) => void
  zoom: number
  onZoomChange: (zoom: number) => void
  showGrid?: boolean
  onToggleGrid?: () => void
}

const ZOOM_LEVELS = [1, 2, 3, 4]

export function MapActions({
  mapData,
  spriteData,
  selectedTile,
  onMapChange,
  zoom,
  onZoomChange,
  showGrid = true,
  onToggleGrid,
}: MapActionsProps) {
  const csvInputRef = useRef<HTMLInputElement>(null)
  const { showConfirmDialog, confirmDialog } = useConfirmDialog()

  // Clear map with confirmation
  const handleClear = useCallback(async () => {
    const confirmed = await showConfirmDialog({
      title: 'Clear Map',
      message: 'Clear entire map? This cannot be undone.',
      confirmLabel: 'Clear',
      confirmVariant: 'danger',
    })
    if (confirmed) {
      onMapChange(clearMap())
    }
  }, [onMapChange, showConfirmDialog])

  // Fill map with selected tile
  const handleFillAll = useCallback(async () => {
    const confirmed = await showConfirmDialog({
      title: 'Fill Map',
      message: `Fill entire map with tile #${selectedTile}? This cannot be undone.`,
      confirmLabel: 'Fill',
      confirmVariant: 'danger',
    })
    if (confirmed) {
      onMapChange(fillMapWithTile(selectedTile))
    }
  }, [selectedTile, onMapChange, showConfirmDialog])

  // Export as CSV
  const handleExportCsv = useCallback(() => {
    downloadMapAsCsv(mapData, 'map.csv')
  }, [mapData])

  // Import from CSV
  const handleImportCsv = useCallback(() => {
    csvInputRef.current?.click()
  }, [])

  const handleCsvFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      try {
        const newMap = await importMapFromFile(file)
        onMapChange(newMap)
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to import CSV')
      }

      // Reset input
      e.target.value = ''
    },
    [onMapChange],
  )

  // Export as PNG
  const handleExportPng = useCallback(() => {
    exportMapAsPng(mapData, spriteData, 'map.png')
  }, [mapData, spriteData])

  return (
    <div className={`${editorStyles.actionGroupContainer} ${styles.container}`}>
      {/* Map Operations */}
      <ActionGroup label='Map'>
        <IconButton
          onClick={handleClear}
          title='Clear Map'
          aria-label='Clear map'
        >
          <ClearIcon />
        </IconButton>
        <IconButton
          onClick={handleFillAll}
          title='Fill All'
          aria-label='Fill all with selected tile'
        >
          <FillAllIcon />
        </IconButton>
      </ActionGroup>

      {/* Zoom */}
      <div className={editorStyles.actionGroup}>
        <span className={editorStyles.actionGroupLabel}>Zoom</span>
        <div className={`${editorStyles.buttonGroup} ${styles.zoomButtons}`}>
          {ZOOM_LEVELS.map((level) => (
            <button
              key={level}
              className={`${styles.zoomButton} ${zoom === level ? styles.active : ''}`}
              onClick={() => onZoomChange(level)}
              title={`Zoom ${level}x`}
              aria-label={`Zoom ${level}x`}
              aria-pressed={zoom === level}
            >
              {level}x
            </button>
          ))}
        </div>
      </div>

      {/* View */}
      <ActionGroup label='View'>
        <IconButton
          active={showGrid}
          onClick={onToggleGrid}
          title='Toggle Grid (G)'
          aria-label='Toggle grid'
          aria-pressed={showGrid}
        >
          <GridIcon />
        </IconButton>
      </ActionGroup>

      {/* Import/Export */}
      <ActionGroup label='File'>
        <IconButton
          onClick={handleImportCsv}
          title='Import CSV'
          aria-label='Import CSV'
        >
          <ImportIcon />
        </IconButton>
        <IconButton
          onClick={handleExportCsv}
          title='Export CSV'
          aria-label='Export CSV'
        >
          <ExportIcon />
        </IconButton>
        <IconButton
          onClick={handleExportPng}
          title='Export PNG'
          aria-label='Export PNG'
        >
          <ImageIcon />
        </IconButton>
        <input
          ref={csvInputRef}
          type='file'
          accept='.csv,text/csv'
          onChange={handleCsvFileChange}
          className={styles.hiddenInput}
        />
      </ActionGroup>

      {confirmDialog}
    </div>
  )
}

// Icons unique to map editor
function FillAllIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect
        x='2'
        y='2'
        width='10'
        height='10'
        rx='1'
        fill='currentColor'
        opacity='0.3'
      />
      <rect x='2' y='2' width='10' height='10' rx='1' />
    </svg>
  )
}

function GridIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect x='2' y='2' width='10' height='10' rx='1' />
      <path d='M2 5.5h10M2 9h10M5.5 2v10M9 2v10' />
    </svg>
  )
}

function ImageIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect x='2' y='2' width='10' height='10' rx='1' />
      <circle cx='5' cy='5' r='1' fill='currentColor' />
      <path d='M12 9l-3-3-5 5' />
    </svg>
  )
}
